import React, { useMemo, useState } from "react";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const SOURCE_LABELS = {
  "final result": "Final result",
  algorithm: "Algorithm",
  contributed: "Contributed",
  capture: "Capture",
  "collection site": "Collection site",
  "collection method": "Collection method",
  device: "Device",
};
const TYPE_LABELS = {
  text: "Text",
  enum: "Name or value",
  number: "Number",
  date: "Date",
  datetime: "Date and time",
  tags: "Tags",
};

/** The dictionary as spreadsheet rows. */
export const dictionaryRows = (fields) => [
  [
    "Field",
    "Column in raw data",
    "Description",
    "Comes from",
    "Type",
    "Example",
  ],
  ...fields.map((f) => [
    f.label,
    f.column ?? f.key,
    f.description,
    SOURCE_LABELS[f.source] ?? f.source,
    TYPE_LABELS[f.type] ?? f.type,
    f.example ?? "",
  ]),
];

const cell = {
  px: 1.5,
  py: 1,
  borderBottom: 1,
  borderColor: "divider",
  verticalAlign: "top",
  fontSize: "0.875rem",
};

/**
 * Every field a report or the raw data download can show: its name, its
 * column in the download, what it means and where it comes from, from the
 * same registry the reports themselves read.
 */
export const DataDictionary = ({ fields }) => {
  const [query, setQuery] = useState("");
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return fields;
    return fields.filter((f) =>
      [f.label, f.column, f.key, f.description].some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [fields, query]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        px: { xs: 2, md: 3 },
        py: 2,
        gap: 1.5,
      }}
    >
      <TextField
        size="small"
        placeholder="Search fields"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        inputProps={{ "aria-label": "Search fields" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 360 }}
      />
      <Typography variant="body2" color="text.secondary">
        {shown.length === fields.length
          ? `${fields.length} fields`
          : `${shown.length} of ${fields.length} fields`}
      </Typography>
      <Box
        sx={{
          flex: "0 1 auto",
          minHeight: 0,
          overflow: "auto",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Box
          component="table"
          sx={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}
          aria-label="Data dictionary"
        >
          <Box component="thead">
            <Box component="tr">
              {[
                "Field",
                "Column in raw data",
                "Description",
                "Comes from",
                "Type",
              ].map((h) => (
                <Box
                  component="th"
                  scope="col"
                  key={h}
                  sx={{
                    ...cell,
                    textAlign: "left",
                    fontWeight: 500,
                    position: "sticky",
                    top: 0,
                    bgcolor: "background.paper",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {shown.map((f) => (
              <Box component="tr" key={f.key}>
                <Box
                  component="th"
                  scope="row"
                  sx={{
                    ...cell,
                    textAlign: "left",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {f.label}
                </Box>
                <Box
                  component="td"
                  sx={{
                    ...cell,
                    fontFamily: "monospace",
                    whiteSpace: "nowrap",
                  }}
                >
                  {f.column ?? f.key}
                </Box>
                <Box component="td" sx={{ ...cell, minWidth: 320 }}>
                  {f.description}
                  {f.example && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      For example: {f.example}
                    </Typography>
                  )}
                </Box>
                <Box component="td" sx={{ ...cell, whiteSpace: "nowrap" }}>
                  {SOURCE_LABELS[f.source] ?? f.source}
                </Box>
                <Box component="td" sx={{ ...cell, whiteSpace: "nowrap" }}>
                  {TYPE_LABELS[f.type] ?? f.type}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
